import { inject, Injectable } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { Bundle, Location } from 'fhir/r4';
import { TableFilteringService, TableFilters, TableRowModel } from '../shared/table-filtering.service';
import { backendEndPointToken, upstreamServerEndPointToken, patientBackendEndPointToken } from '../app.config';
import { HttpClient, HttpContext } from '@angular/common/http';
import { LoadingUIEnabled } from '../loading.interceptor';
import { StateService } from '../shared/state.service';
import { BehaviorSubject, EMPTY, Observable, of } from 'rxjs';
import { catchError, expand, map, tap } from 'rxjs/operators';

export interface LocationRow extends TableRowModel {
  /** The display name (possibly enriched with "Ward" prefix/suffix) */
  name?: string;
  /** active | inactive | suspended | unknown */
  status?: string;
  /** Computed availability: "X / Y" available beds for wards/rooms */
  availableBeds?: string;
  /** The immediate parent display string (human-readable) */
  partOf?: string;
  /** Full ancestor hierarchy, e.g. "Room 1, Ward A, Block 2" */
  belongsTo?: string;
  /** Alias used in table cell template */
  _hierarchyString?: string;
  /** Physical type code, e.g. "ward", "room", "bed" */
  physicalTypeCode?: string;
  /** For beds: "Free" | "Occupied" */
  'Occupancy Status'?: string;
  /** For beds/rooms: name of the direct parent room */
  Room?: string;
  /** For beds/rooms: name of the parent ward */
  Ward?: string;
}

@Injectable({ providedIn: 'root' })
export class LocationSearchService {
  private readonly http = inject(HttpClient);
  private readonly stateService = inject(StateService);
  private readonly tableFiltering = inject(TableFilteringService) as unknown as TableFilteringService<Location, LocationRow>;
  private readonly backendUrl = inject(backendEndPointToken);
  private readonly upstreamBase = inject(upstreamServerEndPointToken);
  private readonly patientBackendBase = inject(patientBackendEndPointToken, { optional: true });
  private readonly silentHttpContext = new HttpContext().set(LoadingUIEnabled, false);

  readonly dataSource = new MatTableDataSource<LocationRow>();
  readonly loading$ = this.tableFiltering.loading$;
  readonly total$ = this.tableFiltering.total$;
  readonly error$ = this.tableFiltering.error$;
  readonly displayedTotal$ = this.tableFiltering.displayedTotal$;
  readonly unknownTotal$ = this.tableFiltering.unknownTotal$;

  /** Emits true while the initial full-fetch is in progress. */
  readonly fetching$ = new BehaviorSubject<boolean>(false);

  /** Initialize local filtering mode with an empty row set.
   *  Rows are pushed in via setLocalRows() once fetched. */
  initLocal(filterPredicate?: (row: LocationRow, filters: TableFilters) => boolean): void {
    this.tableFiltering.init({
      resourceType: 'Location',
      defaultQuery: '/Location',
      dataSource: this.dataSource,
      pageSize: 50,
      baseUrl: this.backendUrl,
      enableLocalFiltering: true,
      localRows: [],
      localFilterPredicate: filterPredicate ?? ((row, filters) => this.defaultLocalFilter(row, filters)),
      httpOptions: { context: this.silentHttpContext },
      disableCountQuery: true
    });
  }

  /**
   * Smart fetch protocol:
   * 1. Queries backend with GET /Location?_summary=count to check total count on server.
   * 2. Compares server total against cached locations in StateService.
   * 3. If StateService has >= serverTotal locations, uses StateService immediately without extra HTTP requests!
   * 4. Otherwise, executes fetchAll() to retrieve all pages via next links and syncs with StateService.
   */
  ensureAllLocationsLoaded(onPage: (allLocations: Location[]) => void): void {
    const countUrl = `${this.backendUrl}/Location?_summary=count`;

    this.http.get<Bundle<Location>>(countUrl, { context: this.silentHttpContext }).pipe(
      catchError(() => of({ total: 0 } as Bundle<Location>))
    ).subscribe((countBundle) => {
      const serverTotal = countBundle?.total ?? 0;
      const cachedEntries = this.stateService.orgWideResources.locations.getValue() ?? [];
      const cachedLocations = cachedEntries.map(e => e.actualResource).filter(Boolean);

      if (serverTotal > 0 && cachedLocations.length >= serverTotal) {
        console.log(`[LocationSearch] StateService already has all ${cachedLocations.length} locations (server total: ${serverTotal}). Skipping fetchAll.`);
        this.fetching$.next(false);
        onPage(cachedLocations);
      } else {
        console.log(`[LocationSearch] Fetching all locations (StateService has ${cachedLocations.length}, server total: ${serverTotal})...`);
        this.fetchAll(onPage);
      }
    });
  }

  /**
   * Fetch ALL Location resources from the server by following next links.
   * Emits accumulated pages as they arrive so callers can update progressively.
   *
   * @param onPage - called with the full accumulated list after each page
   */
  fetchAll(onPage: (allSoFar: Location[]) => void): void {
    const firstUrl = `${this.backendUrl}/Location?_count=200&_sort=-_lastUpdated`;
    const accumulated: Location[] = [];
    this.fetching$.next(true);

    this.http.get<Bundle<Location>>(firstUrl, { context: this.silentHttpContext }).pipe(
      expand(bundle => {
        // Collect resources from this page
        const resources = (bundle.entry ?? [])
          .map(e => e.resource as Location)
          .filter(Boolean);
        accumulated.push(...resources);
        onPage([...accumulated]);

        // Follow next link if present, normalizing URL to backend base
        const rawNextUrl = (bundle.link ?? []).find(l => l.relation === 'next')?.url;
        const nextUrl = rawNextUrl ? this.normalizeNextLink(rawNextUrl) : null;
        return nextUrl ? this.http.get<Bundle<Location>>(nextUrl, { context: this.silentHttpContext }) : EMPTY;
      }),
      catchError(() => EMPTY)
    ).subscribe({
      complete: () => {
        this.fetching$.next(false);
        // Final emit to make sure the last page is reflected
        onPage([...accumulated]);
      },
      error: () => this.fetching$.next(false)
    });
  }

  private normalizeNextLink(nextLink: string): string {
    try {
      const backend = (this.backendUrl ?? '').toString().replace(/\/$/, '');
      const upstream = (this.upstreamBase ?? '').toString().replace(/\/$/, '');
      const patientBackend = (this.patientBackendBase ?? '').toString().replace(/\/$/, '');
      if (!backend || !nextLink) {
        return nextLink;
      }

      if (nextLink.startsWith(backend) || (patientBackend && nextLink.startsWith(patientBackend))) {
        return nextLink;
      }

      if (upstream && nextLink.startsWith(upstream)) {
        return backend + nextLink.substring(upstream.length);
      }
    } catch {
      // Keep original server-provided link if parsing fails
    }

    return nextLink;
  }

  /** Feed a fresh set of (enriched) rows to the local filtering engine. */
  setLocalRows(rows: LocationRow[]): void {
    this.tableFiltering.enableLocalFiltering(rows);
  }

  /** Trigger a search using key/value pairs matched against LocationRow fields. */
  search(filters: TableFilters): void {
    this.tableFiltering.search(filters);
  }

  changePage(pageIndex: number): void {
    this.tableFiltering.changePage(pageIndex);
  }

  refresh(): void {
    this.tableFiltering.refreshCurrentPage();
  }

  destroy(): void {
    this.tableFiltering.destroy();
    this.dataSource.disconnect();
    this.fetching$.next(false);
  }

  // ─── Transform (raw Location → LocationRow) ───────────────────────────────

  transform(resource: Location): LocationRow {
    const coding = resource.physicalType?.coding ?? [];
    const physicalTypeCode =
      coding[0]?.code?.toLowerCase() ??
      resource.physicalType?.text?.toLowerCase() ??
      '';

    return {
      id: resource.id ?? '',
      resourceType: 'Location',
      rawResource: resource,
      name: resource.name,
      status: resource.status,
      physicalTypeCode,
      availableBeds: (resource as any).availableBeds,
      partOf: (resource as any).partOf,
      belongsTo: (resource as any).belongsTo,
      _hierarchyString: (resource as any)._hierarchyString,
      'Occupancy Status': (resource as any)['Occupancy Status'],
      Room: (resource as any)['Room'],
      Ward: (resource as any)['Ward'],
    } as LocationRow;
  }

  // ─── Default local filter predicate ──────────────────────────────────────────

  private defaultLocalFilter(row: LocationRow, filters: TableFilters): boolean {
    const entries = Object.entries(filters ?? {});
    if (!entries.length) return true;

    return entries.every(([key, constraint]) => {
      if (constraint === undefined || constraint === null || String(constraint).trim() === '') return true;

      // Tab filter: physicalType
      if (key === '_physicalType') {
        const val = String(constraint).toLowerCase().trim();
        if (val === 'all') return true;
        const raw = row.rawResource as Location;
        const typeText = raw.physicalType?.text?.toLowerCase() ?? '';
        const matchesCode = (raw.physicalType?.coding ?? []).some(
          c => c.code?.toLowerCase() === val || c.display?.toLowerCase() === val
        );
        return typeText === val || matchesCode;
      }

      // Ward chip filter (Rooms tab)
      if (key === '_wardChip') {
        return row['Ward'] === String(constraint);
      }

      // Free-text search across name + hierarchy
      if (key === '_search') {
        const term = String(constraint).toLowerCase();
        const nameLower = (row.name ?? '').toLowerCase();
        const hierarchyLower = (row._hierarchyString ?? '').toLowerCase();
        return nameLower.includes(term) || hierarchyLower.includes(term);
      }

      // Checkbox filter values may be comma-separated (one of the values must match)
      const constraintStr = String(constraint);
      const constraintValues = constraintStr.includes(',')
        ? constraintStr.split(',').map(v => v.trim().toLowerCase())
        : [constraintStr.toLowerCase()];

      const rowVal = String((row as any)[key] ?? '').toLowerCase();
      return constraintValues.some(v => rowVal === v);
    });
  }
}
